/**
 * Solar position, NOAA/Meeus, in the browser.
 *
 * The yield model gets its sun from pvlib (NREL SPA) on the server. This is the
 * same geometry recomputed client-side so a slider can move the sun without a
 * round trip per frame. The two agree to well under a hundredth of a degree over
 * the range this is used for - verified against pvlib in rowShading.test.js
 * rather than asserted here - which is far below anything a shadow picture or a
 * shaded fraction can show.
 *
 * Angles in degrees. Azimuth is measured clockwise from true north, matching
 * pvlib and every other azimuth in this application.
 *
 * `zenith` and `elevation` are APPARENT - refraction included - because that is
 * the direction the beam actually arrives from, and it is what the rest of the
 * platform runs on: pvlib's apparent_zenith drives the transposition, and
 * Site_spacing_info sizes the row pitch from apparent_elevation. This used to
 * return the geometric position with a comment claiming the difference only
 * mattered within half a degree of the horizon. It is 0.08-0.09 degrees at 10
 * degrees of elevation - which is exactly where winter-solstice row spacing is
 * decided - and at the 09:00 design boundary that is 0.11 m of pitch, enough to
 * leave ~1% of the collector shaded in a picture of a plant the engine had just
 * declared shadow-free at that instant. The geometric values are still returned
 * as trueZenith / trueElevation for anything that wants the unbent sun.
 */

const DEG = Math.PI / 180;
const rad = (d) => d * DEG;
const deg = (r) => r / DEG;

/** Julian day from a UTC instant. */
export function julianDay(dateUtc) {
  return dateUtc.getTime() / 86400000 + 2440587.5;
}

/**
 * Sun's geometric position for an instant and a place.
 *
 * @param {Date}   when  the instant, as a real Date (its UTC value is used)
 * @param {number} lat   degrees north
 * @param {number} lon   degrees east
 * @returns {{zenith:number, elevation:number, azimuth:number,
 *            declination:number, equationOfTime:number, hourAngle:number}}
 */
export function solarPosition(when, lat, lon) {
  const jd = julianDay(when);
  const t = (jd - 2451545.0) / 36525.0;              // Julian centuries J2000

  // Mean elements
  const L0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  const M = 357.52911 + t * (35999.05029 - 0.0001537 * t);
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);

  // Equation of centre -> true longitude -> apparent longitude
  const C =
    Math.sin(rad(M)) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(rad(2 * M)) * (0.019993 - 0.000101 * t) +
    Math.sin(rad(3 * M)) * 0.000289;
  const trueLong = L0 + C;
  const omega = 125.04 - 1934.136 * t;
  const appLong = trueLong - 0.00569 - 0.00478 * Math.sin(rad(omega));

  // Obliquity, with the nutation term
  const seconds = 21.448 - t * (46.815 + t * (0.00059 - t * 0.001813));
  const eps0 = 23 + (26 + seconds / 60) / 60;
  const eps = eps0 + 0.00256 * Math.cos(rad(omega));

  const declination = deg(
    Math.asin(Math.sin(rad(eps)) * Math.sin(rad(appLong)))
  );

  // Equation of time, in minutes
  const y = Math.tan(rad(eps / 2)) ** 2;
  const eqTime =
    4 *
    deg(
      y * Math.sin(2 * rad(L0)) -
        2 * e * Math.sin(rad(M)) +
        4 * e * y * Math.sin(rad(M)) * Math.cos(2 * rad(L0)) -
        0.5 * y * y * Math.sin(4 * rad(L0)) -
        1.25 * e * e * Math.sin(2 * rad(M))
    );

  // True solar time -> hour angle. UTC minutes of the day, so the caller's
  // timezone handling stays in one place (the caller builds the Date).
  const utcMinutes =
    when.getUTCHours() * 60 +
    when.getUTCMinutes() +
    when.getUTCSeconds() / 60 +
    when.getUTCMilliseconds() / 60000;
  const trueSolarTime = (utcMinutes + eqTime + 4 * lon + 1440) % 1440;
  const hourAngle = trueSolarTime / 4 - 180;         // degrees, negative = morning

  const latR = rad(lat);
  const decR = rad(declination);
  const haR = rad(hourAngle);

  const cosZenith =
    Math.sin(latR) * Math.sin(decR) +
    Math.cos(latR) * Math.cos(decR) * Math.cos(haR);
  const zenith = deg(Math.acos(Math.min(1, Math.max(-1, cosZenith))));

  // Azimuth clockwise from north. atan2 form: stable at the poles and at noon,
  // unlike the acos form NOAA publishes, which loses precision near zenith.
  const azimuth =
    (deg(
      Math.atan2(
        Math.sin(haR),
        Math.cos(haR) * Math.sin(latR) - Math.tan(decR) * Math.cos(latR)
      )
    ) +
      180) %
    360;

  // Bennett (1982), the same correction pvlib applies by default: within about
  // 0.07 degrees of the standard tables above the horizon. Meaningless once the
  // sun is well below it, so it is not extrapolated there.
  const trueElevation = 90 - zenith;
  const bend =
    trueElevation > -1
      ? Math.max(
          0,
          1 / Math.tan(rad(trueElevation + 7.31 / (trueElevation + 4.4))) / 60
        )
      : 0;
  const apparentElevation = trueElevation + bend;

  return {
    zenith: 90 - apparentElevation,
    elevation: apparentElevation,
    azimuth: (azimuth + 360) % 360,
    trueZenith: zenith,
    trueElevation,
    refraction: bend,
    declination,
    equationOfTime: eqTime,
    hourAngle,
  };
}

/**
 * Sunrise and sunset for a date, as minutes after local midnight.
 *
 * Used to bound the time slider so the useful hours get the whole track instead
 * of a third of it. Returns null when the sun does not rise or set that day.
 */
export function daylightWindow(year, month, day, lat, lon, utcOffsetHours) {
  const decl = solarPosition(
    new Date(Date.UTC(year, month - 1, day, 12, 0, 0)),
    lat,
    lon
  ).declination;
  const cosH =
    (Math.cos(rad(90.833)) - Math.sin(rad(lat)) * Math.sin(rad(decl))) /
    (Math.cos(rad(lat)) * Math.cos(rad(decl)));
  if (cosH > 1 || cosH < -1) return null;            // polar night / midnight sun

  const H = deg(Math.acos(cosH));                    // half-day arc, degrees
  const eqTime = solarPosition(
    new Date(Date.UTC(year, month - 1, day, 12, 0, 0)),
    lat,
    lon
  ).equationOfTime;
  const solarNoonUtcMin = 720 - 4 * lon - eqTime;
  const offsetMin = utcOffsetHours * 60;
  return {
    sunrise: solarNoonUtcMin - 4 * H + offsetMin,
    sunset: solarNoonUtcMin + 4 * H + offsetMin,
    solarNoon: solarNoonUtcMin + offsetMin,
  };
}

/**
 * The offset a real timezone is on at an instant, in hours - daylight saving
 * included.
 *
 * The viewer runs on the site's STANDARD offset all year, because that is the
 * time base the simulation runs on (the TMY is placed at a fixed offset; a
 * viewer that followed daylight saving would show the sun an hour off its own
 * model for half the year). That is right, and it is also why a summer sunset
 * reads an hour earlier here than on the wall clock at the site - which looks
 * like an error unless the picture says both. This is how it gets the second
 * number. Returns null where the runtime cannot answer, and the caller then
 * shows the model's clock alone, as before.
 */
export function zoneOffsetHours(timeZone, when) {
  if (!timeZone) return null;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
      hour: "numeric",
    }).formatToParts(when instanceof Date ? when : new Date(when));
    const name = (parts.find((p) => p.type === "timeZoneName") || {}).value || "";
    if (/^GMT$|^UTC$/.test(name.trim())) return 0;
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
    if (!m) return null;
    return (m[1] === "-" ? -1 : 1) * (Number(m[2]) + Number(m[3] || 0) / 60);
  } catch {
    return null;
  }
}

/** The instant for a local wall-clock time at a fixed UTC offset. */
export function instantFromLocal(year, month, day, minutesOfDay, utcOffsetHours) {
  return new Date(
    Date.UTC(year, month - 1, day, 0, 0, 0) +
      (minutesOfDay - utcOffsetHours * 60) * 60000
  );
}
